/**
 * Vertical tab navigation for the profile page.
 *
 * Rolled by hand instead of using AnimatedTabs because that one is
 * horizontal-only. Keeps semantic tokens, no hard-coded colors.
 */
import * as React from 'react';
import { cn } from '@finmin/ui';

export interface ProfileTabItem<T extends string = string> {
  key: T;
  label: string;
  icon?: React.ReactNode;
  badge?: React.ReactNode;
}

interface Props<T extends string> {
  tabs: ProfileTabItem<T>[];
  active: T;
  onChange: (key: T) => void;
}

export function ProfileTabSidebar<T extends string>({
  tabs,
  active,
  onChange,
}: Props<T>) {
  return (
    <nav className="flex flex-row gap-1 overflow-x-auto md:flex-col md:gap-0.5 md:overflow-visible">
      {tabs.map((tab) => {
        const isActive = tab.key === active;
        return (
          <button
            key={tab.key}
            type="button"
            onClick={() => onChange(tab.key)}
            aria-current={isActive ? 'page' : undefined}
            className={cn(
              'shrink-0 md:shrink rounded-md px-3 py-2 text-left text-sm transition-colors',
              'flex items-center gap-2',
              isActive
                ? 'bg-secondary text-foreground font-semibold'
                : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50',
            )}
          >
            {tab.icon && <span className="shrink-0">{tab.icon}</span>}
            <span className="flex-1 truncate">{tab.label}</span>
            {tab.badge}
          </button>
        );
      })}
    </nav>
  );
}
